import { FormEvent, useEffect, useState } from 'react'
import styled from 'styled-components'

/** The Launch Challenge application form: one page, one POST to /api/challenge/apply. */
const OPEN_AT = Date.parse('2026-10-15T04:00:00Z')
const CLOSE_AT = Date.parse('2026-11-16T05:00:00Z')

const Wrap = styled.div`
  max-width: 680px;
  margin: 0 auto;
  padding: 28px 16px 64px;
  color: #f1f4ff;
  h1 {
    font-size: clamp(30px, 6vw, 44px);
    line-height: 1.1;
    margin: 0 0 10px;
    font-weight: 800;
  }
  p {
    margin: 0 0 14px;
    line-height: 1.6;
    color: #a9b4dc;
    font-size: 17px;
  }
`
const Facts = styled.ul`
  margin: 0 0 22px;
  padding: 14px 16px 14px 34px;
  background: #10163a;
  border: 1px solid #28336b;
  border-radius: 14px;
  color: #dfe6ff;
  line-height: 1.7;
`
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #10163a;
  border: 1px solid #28336b;
  border-radius: 18px;
  padding: 20px;
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 15px;
    font-weight: 600;
    color: #f1f4ff;
  }
  small {
    font-weight: 400;
    color: #a9b4dc;
  }
  input,
  select,
  textarea {
    font: inherit;
    font-size: 17px;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid #3a4780;
    background: #070b1a;
    color: #f1f4ff;
    width: 100%;
  }
  textarea {
    min-height: 120px;
    resize: vertical;
  }
  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  @media (max-width: 560px) {
    .row {
      grid-template-columns: 1fr;
    }
  }
  .err {
    color: #ff8a97;
    font-size: 14px;
    font-weight: 500;
  }
  .check {
    flex-direction: row;
    align-items: flex-start;
    font-weight: 400;
    color: #a9b4dc;
  }
  .check input {
    width: 22px;
    height: 22px;
    margin-top: 2px;
  }
  .hp {
    position: absolute;
    left: -9999px;
    height: 0;
    overflow: hidden;
  }
  button {
    font: inherit;
    font-weight: 700;
    font-size: 18px;
    padding: 15px;
    border-radius: 14px;
    border: 0;
    background: #8b6bff;
    color: #fff;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`
const Notice = styled.div<{ $tone: 'ok' | 'warn' | 'bad' }>`
  padding: 14px 16px;
  border-radius: 14px;
  margin-bottom: 18px;
  font-size: 16px;
  line-height: 1.5;
  background: ${({ $tone }) => ($tone === 'ok' ? '#10331f' : $tone === 'warn' ? '#3a2a0a' : '#3a1217')};
  color: ${({ $tone }) => ($tone === 'ok' ? '#7ff0a8' : $tone === 'warn' ? '#ffd98a' : '#ff9aa6')};
`

const EMPTY = { projectName: '', symbol: '', contract: '', stage: '', track: '', link: '', description: '', contact: '', consent: false, website: '' }

export default function LaunchChallenge() {
  const [now, setNow] = useState<number | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  useEffect(() => setNow(Date.now()), [])

  const state = now === null ? 'open' : now < OPEN_AT ? 'before' : now >= CLOSE_AT ? 'closed' : 'open'
  const set = (k: keyof typeof EMPTY, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFailure(null)
    setErrors({})
    try {
      const r = await fetch('/api/challenge/apply', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(form) })
      const j = await r.json().catch(() => ({}))
      if (r.ok && j.ok) {
        setDone(j.id)
        setForm(EMPTY)
      } else if (j.errors) setErrors(j.errors)
      else if (j.error === 'duplicate') setFailure('This contract has already applied. One application per project.')
      else if (j.error === 'not_open_yet') setFailure('Applications open on October 15, 2026.')
      else if (j.error === 'closed') setFailure('Applications are closed.')
      else if (j.error === 'rate_limited') setFailure('Too many attempts. Please try again in an hour.')
      else setFailure('Something went wrong on our side. Please try again in a few minutes.')
    } catch {
      setFailure('The request did not go through. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  const err = (k: string) => (errors[k] ? <span className="err">{errors[k]}</span> : null)

  return (
    <Wrap>
      <h1>CADINU Launch Challenge: apply</h1>
      <p>Up to $39,000 of support for six projects, chosen by the CADINU DAO in two lanes: the main lane (three projects) and the fun lane for meme coins (three projects). Build it. Launch it. Grow it.</p>
      <Facts>
        <li>Applications: October 15 to November 15, 2026</li>
        <li>DAO vote and competition: November 15 to 30, 2026</li>
        <li>To reach the vote: token launched through CAD1, 200 unique holders, market cap above $20,000, $500 daily volume for seven days, and a public whitepaper</li>
        <li>Main lane: idea, MVP or live product in DeFi, gaming or an open topic. Fun lane: meme coins. Each lane is voted separately</li>
      </Facts>

      {state === 'before' && <Notice $tone="warn">Applications open on October 15, 2026. Come back then.</Notice>}
      {state === 'closed' && <Notice $tone="warn">Applications are closed. The initiative continues periodically; follow CADINU for the next round.</Notice>}
      {done && <Notice $tone="ok">Received. Your application id is {done}. We will contact you on the handle you gave us.</Notice>}
      {failure && <Notice $tone="bad">{failure}</Notice>}

      <Form onSubmit={submit} noValidate>
        <label htmlFor="ch-projectName">
          Project name
          <input id="ch-projectName" value={form.projectName} onChange={(e) => set('projectName', e.target.value)} maxLength={60} autoComplete="off" />
          {err('projectName')}
        </label>
        <div className="row">
          <label htmlFor="ch-symbol">
            Token symbol
            <input id="ch-symbol" value={form.symbol} onChange={(e) => set('symbol', e.target.value)} maxLength={12} autoComplete="off" />
            {err('symbol')}
          </label>
          <label htmlFor="ch-contract">
            Token contract on BNB Chain
            <small>Leave empty only at idea stage</small>
            <input id="ch-contract" value={form.contract} onChange={(e) => set('contract', e.target.value)} placeholder="0x..." maxLength={42} autoComplete="off" />
            {err('contract')}
          </label>
        </div>
        <div className="row">
          <label htmlFor="ch-stage">
            Stage
            <select id="ch-stage" value={form.stage} onChange={(e) => set('stage', e.target.value)}>
              <option value="">Choose</option>
              <option value="idea">Idea</option>
              <option value="mvp">MVP</option>
              <option value="live">Live product</option>
            </select>
            {err('stage')}
          </label>
          <label htmlFor="ch-track">
            Track
            <select id="ch-track" value={form.track} onChange={(e) => set('track', e.target.value)}>
              <option value="">Choose</option>
              <option value="defi">DeFi</option>
              <option value="gaming">Gaming</option>
              <option value="open">Open topic</option>
              <option value="meme">Fun lane: meme coin</option>
            </select>
            {err('track')}
          </label>
        </div>
        <label htmlFor="ch-link">
          Whitepaper or project page
          <input id="ch-link" value={form.link} onChange={(e) => set('link', e.target.value)} placeholder="https://" maxLength={300} inputMode="url" autoComplete="off" />
          {err('link')}
        </label>
        <label htmlFor="ch-description">
          What are you building?
          <small>One short paragraph: the product, who it is for, and what comes next.</small>
          <textarea id="ch-description" value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={700} />
          {err('description')}
        </label>
        <label htmlFor="ch-contact">
          Contact: Telegram or X handle
          <input id="ch-contact" value={form.contact} onChange={(e) => set('contact', e.target.value)} placeholder="@yourname" maxLength={100} autoComplete="off" />
          {err('contact')}
        </label>
        <div className="hp" aria-hidden="true">
          <label htmlFor="ch-website">
            Website
            <input id="ch-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} />
          </label>
        </div>
        <label htmlFor="ch-consent" className="check">
          <input id="ch-consent" type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} />
          <span>
            I confirm the information is accurate and I agree that holders and volume are checked on-chain. Being listed or voted for is not an endorsement by CADINU.
          </span>
        </label>
        {err('consent')}
        <button type="submit" disabled={busy || state !== 'open'}>
          {busy ? 'Sending...' : 'Submit application'}
        </button>
      </Form>
    </Wrap>
  )
}
